import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  LIMITS,
  contractFieldsSchema,
  contractWarnings,
  crossFieldIssues,
  formatIssues,
  nextNoticeDeadline,
  normalizeFields,
  parseTagInput,
  withLegacyMoved,
  type ContractFields,
} from "../../shared/contract";
import { formatDate } from "../../shared/dates";
import { currencyCodes, currencyDigits } from "../../shared/money";
import type { Suggestions } from "../../shared/suggest";
import { typeFor } from "../../shared/catalog";
import type { AiSuggestions } from "../../shared/ai";
import { PartiesEditor, TermsEditor, TypePicker, type DetailRow, type PartyRow } from "./ContractExtras";
import { Alert, Button, Field, Select, TextArea, TextInput } from "./ui";

export type NoticeUnit = "days" | "weeks" | "months";

export interface FormState {
  title: string;
  currency: string;
  startDate: string;
  endDate: string;
  renewalType: ContractFields["renewalType"];
  renewalTermMonths: string;
  notice: string;
  noticeUnit: NoticeUnit;
  ownerUserId: string;
  tags: string;
  notes: string;
  type: string;
  parties: PartyRow[];
  details: DetailRow[];
}

export type FormErrors = Partial<Record<keyof FormState, string>>;

export function stateFromFields(original: ContractFields, defaultCurrency = ""): FormState {
  // An old counterparty and value become a person and the "Total contract value" term.
  const f = withLegacyMoved(original);
  return {
    title: f.title,
    currency: f.currency ?? defaultCurrency,
    startDate: f.startDate ?? "",
    endDate: f.endDate ?? "",
    renewalType: f.renewalType,
    renewalTermMonths: f.renewalTermMonths === null ? "" : String(f.renewalTermMonths),
    notice: f.noticePeriodDays === null ? "" : String(f.noticePeriodDays),
    noticeUnit: "days",
    ownerUserId: f.ownerUserId ?? "",
    tags: f.tags.join(", "),
    notes: f.notes ?? "",
    type: f.type ?? "",
    parties: f.parties.map((p) => ({ name: p.name, role: p.role, email: p.email ?? "", phone: p.phone ?? "" })),
    details: Object.entries(f.details).map(([key, value]) => ({ key, value })),
  };
}

/** "30", " 30 ", "30 days" → 30; "" → null; "thirty", "-3", "1.5" → error. */
export function parseWholeNumber(input: string): { ok: true; value: number | null } | { ok: false } {
  const s = input.trim().replace(/\s*(days?|weeks?|months?)$/i, "");
  if (s === "") return { ok: true, value: null };
  if (!/^\d{1,6}$/.test(s)) return { ok: false };
  return { ok: true, value: Number(s) };
}

const UNIT_DAYS: Record<NoticeUnit, number> = { days: 1, weeks: 7, months: 30 };

const FIELD_FOR: Record<string, keyof FormState> = {
  renewalTermMonths: "renewalTermMonths",
  noticePeriodDays: "notice",
};

/** Converts what's in the form into contract fields, or field-level errors. */
export function fieldsFromState(s: FormState, status: ContractFields["status"] = "active"): { ok: true; fields: ContractFields } | { ok: false; errors: FormErrors } {
  const errors: FormErrors = {};
  const currency = s.currency.trim().toUpperCase();

  const notice = parseWholeNumber(s.notice);
  if (!notice.ok) errors.notice = "Enter a whole number, like 30.";
  // The term field is hidden when the contract doesn't renew; whatever is left in it doesn't count.
  const term = s.renewalType === "none" ? ({ ok: true, value: null } as const) : parseWholeNumber(s.renewalTermMonths);
  if (!term.ok) errors.renewalTermMonths = "Enter a whole number of months, like 12.";

  const noticeDays = notice.ok && notice.value !== null ? notice.value * UNIT_DAYS[s.noticeUnit] : null;
  const parsed = contractFieldsSchema.safeParse({
    title: s.title,
    counterparty: null,
    valueMinor: null,
    currency: currency || null,
    startDate: s.startDate.trim(),
    endDate: s.endDate.trim(),
    renewalType: s.renewalType,
    renewalTermMonths: s.renewalType === "none" || !term.ok ? null : term.value,
    noticePeriodDays: notice.ok ? noticeDays : null,
    ownerUserId: s.ownerUserId || null,
    tags: parseTagInput(s.tags),
    notes: s.notes,
    status,
    type: s.type || null,
    parties: s.parties,
    // An added term left empty is dropped, like an empty field.
    details: Object.fromEntries(s.details.map((d) => [d.key, d.value])),
  });
  if (!parsed.success) {
    for (const issue of formatIssues(parsed.error)) {
      const key = (FIELD_FOR[issue.path] ?? issue.path) as keyof FormState;
      errors[key] ??= issue.message;
    }
  }
  if (Object.keys(errors).length || !parsed.success) return { ok: false, errors };

  const fields = normalizeFields(parsed.data);
  for (const issue of crossFieldIssues(fields)) {
    const key = (FIELD_FOR[issue.path] ?? issue.path) as keyof FormState;
    errors[key] ??= issue.message;
  }
  return Object.keys(errors).length ? { ok: false, errors } : { ok: true, fields };
}

/**
 * Adds AI suggestions to a form the user may already have edited (a full scan after a quick one),
 * filling only what's empty and adding people and terms that aren't there yet.
 */
export function mergeAiIntoFormState(s: FormState, ai: AiSuggestions): { state: FormState; added: number; keys: string[] } {
  const next: FormState = { ...s, parties: [...s.parties], details: [...s.details] };
  let added = 0;
  const fill = <K extends keyof FormState>(key: K, value: FormState[K] | undefined) => {
    if (value === undefined || value === "" || (typeof next[key] === "string" && next[key] !== "")) return;
    next[key] = value;
    added++;
  };
  fill("type", ai.type);
  fill("startDate", ai.startDate);
  fill("endDate", ai.endDate);
  if (ai.noticePeriodDays !== undefined && next.notice === "") {
    next.notice = String(ai.noticePeriodDays);
    next.noticeUnit = "days";
    added++;
  }
  if (ai.renewalType && ai.renewalType !== "none" && next.renewalType === "none") {
    next.renewalType = ai.renewalType;
    added++;
  }
  if (ai.renewalTermMonths !== undefined && next.renewalType !== "none") fill("renewalTermMonths", String(ai.renewalTermMonths));
  if (ai.value && !next.details.some((d) => d.key === "contract_value" && d.value)) {
    const digits = currencyDigits(ai.value.currency);
    const value = (ai.value.minor / 10 ** digits).toFixed(digits).replace(/\.0+$/, "");
    const row = next.details.find((d) => d.key === "contract_value");
    if (row) row.value = value;
    else next.details.push({ key: "contract_value", value });
    if (!next.currency) next.currency = ai.value.currency;
    added++;
  }
  for (const p of ai.parties ?? []) {
    if (next.parties.some((x) => x.name.trim().toLowerCase() === p.name.toLowerCase())) continue;
    next.parties.push({ name: p.name, role: p.role, email: p.email ?? "", phone: p.phone ?? "" });
    added++;
  }
  const keys: string[] = [];
  for (const [key, value] of Object.entries(ai.details ?? {})) {
    const row = next.details.find((d) => d.key === key);
    if (row && row.value) continue;
    if (row) row.value = value;
    else next.details.push({ key, value });
    keys.push(key);
    added++;
  }
  return { state: next, added, keys };
}

interface Props {
  initial: ContractFields;
  /** A type guessed from the document, offered with one click. */
  suggestedType?: string | null;
  /** Terms filled in by AI, labelled in the form. */
  aiKeys?: ReadonlySet<string>;
  /** Later AI results (a full scan) merged into the form without overwriting edits; a new `id` applies it. */
  aiPatch?: { id: number; suggestions: AiSuggestions } | null;
  /** Called with how much a patch added. */
  onAiPatched?: (added: number, keys: string[]) => void;
  defaultCurrency?: string;
  members: { id: string; email: string; name: string | null }[];
  /** Shown as "Me" in the owner list and used as the default owner. */
  currentUserId?: string;
  suggestions?: Suggestions;
  today: string;
  submitLabel: string;
  onSubmit: (fields: ContractFields) => Promise<void>;
  onCancel?: () => void;
}

export function ContractForm({ initial, suggestedType, aiKeys, aiPatch, onAiPatched, defaultCurrency = "", members, currentUserId, suggestions, today, submitLabel, onSubmit, onCancel }: Props) {
  const [state, setState] = useState<FormState>(() => {
    const s = stateFromFields(initial, defaultCurrency);
    return s.ownerUserId || !currentUserId || !members.some((m) => m.id === currentUserId) ? s : { ...s, ownerUserId: currentUserId };
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const currencies = useMemo(currencyCodes, []);
  const appliedPatch = useRef<number | null>(null);

  useEffect(() => {
    if (!aiPatch || appliedPatch.current === aiPatch.id) return;
    appliedPatch.current = aiPatch.id;
    const { state: next, added, keys } = mergeAiIntoFormState(state, aiPatch.suggestions);
    setState(next);
    onAiPatched?.(added, keys);
    // Only when a new patch arrives; the current state is read once, at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiPatch]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((s) => ({ ...s, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
    setDirty(true);
  };

  const preview = fieldsFromState(state, initial.status);
  const warnings = preview.ok ? contractWarnings(preview.fields, today) : [];
  const deadline = preview.ok ? nextNoticeDeadline(preview.fields, today) : null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    const result = fieldsFromState(state, initial.status);
    if (!result.ok) {
      setErrors(result.errors);
      const first = Object.keys(result.errors)[0];
      document.querySelector<HTMLElement>(`[name="${first}"]`)?.focus();
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await onSubmit(result.fields);
      setDirty(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const hint = (key: Exclude<keyof Suggestions, "details">) => {
    const s = suggestions?.[key];
    if (!s) return undefined;
    return (
      <span title={s.evidence}>
        Suggested from the document{s.note ? ` (${s.note})` : ""}: “{s.evidence.slice(0, 120)}”
      </span>
    );
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-8" aria-label="Contract details">
      <section className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <span className="block text-sm font-medium text-fg">Type</span>
          <TypePicker value={state.type} suggested={suggestedType} onChange={(id) => set("type", id)} />
          {suggestions?.type?.confident && state.type === suggestions.type.value && (
            <p className="text-xs leading-relaxed text-faint">Picked from the document: {suggestions.type.evidence}.</p>
          )}
          {errors.type && (
            <p role="alert" className="text-sm text-bad">
              {errors.type}
            </p>
          )}
        </div>
        <div className="sm:col-span-2">
          <Field label="Contract name" error={errors.title}>
            {(id, d) => (
              <TextInput id={id} name="title" aria-describedby={d} invalid={!!errors.title} value={state.title} maxLength={LIMITS.title + 50} onChange={(e) => set("title", e.target.value)} placeholder="Office lease, Figma subscription…" autoFocus={!initial.title} />
            )}
          </Field>
        </div>
        <Field label="Owner" error={errors.ownerUserId} hint="Gets the reminders.">
          {(id, d) => (
            <Select id={id} name="ownerUserId" aria-describedby={d} value={state.ownerUserId} onChange={(e) => set("ownerUserId", e.target.value)}>
              {!members.some((m) => m.id === currentUserId) && <option value="">Me</option>}
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.id === currentUserId ? `Me (${m.email})` : m.name ? `${m.name} (${m.email})` : m.email}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Currency for amounts" error={errors.currency} hint="Used for the contract value, rent, premium and other amounts.">
          {(id, d) => (
            <Select id={id} name="currency" aria-describedby={d} invalid={!!errors.currency} value={state.currency} onChange={(e) => set("currency", e.target.value)}>
              <option value="">—</option>
              {currencies.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </section>

      <section className="grid gap-4 border-t border-line pt-6 sm:grid-cols-2">
        <h2 className="sm:col-span-2 text-base font-semibold tracking-tight">Dates</h2>
        <Field label="Start date" error={errors.startDate} hint={hint("startDate")}>
          {(id, d) => <TextInput id={id} name="startDate" type="date" min="1900-01-01" max="2200-12-31" aria-describedby={d} invalid={!!errors.startDate} value={state.startDate} onChange={(e) => set("startDate", e.target.value)} />}
        </Field>
        <Field label="End date (current term)" error={errors.endDate} hint={hint("endDate") ?? (suggestions?.noEndDate ? <span title={suggestions.noEndDate.evidence}>The document has no fixed end date: “{suggestions.noEndDate.evidence.slice(0, 120)}”</span> : "When it expires or next renews.")}>
          {(id, d) => <TextInput id={id} name="endDate" type="date" min="1900-01-01" max="2200-12-31" aria-describedby={d} invalid={!!errors.endDate} value={state.endDate} onChange={(e) => set("endDate", e.target.value)} />}
        </Field>
        <Field label="Renewal" error={errors.renewalType} hint={hint("renewalType")}>
          {(id, d) => (
            <Select id={id} name="renewalType" aria-describedby={d} value={state.renewalType} onChange={(e) => set("renewalType", e.target.value as FormState["renewalType"])}>
              <option value="none">Ends on the end date</option>
              <option value="auto">Renews automatically unless cancelled</option>
              <option value="manual">Can be renewed if we choose</option>
            </Select>
          )}
        </Field>
        {state.renewalType !== "none" && (
          <Field label="Renewal term (months)" error={errors.renewalTermMonths} hint={hint("renewalTermMonths") ?? "How long each renewal lasts, e.g. 12."}>
            {(id, d) => <TextInput id={id} name="renewalTermMonths" inputMode="numeric" aria-describedby={d} invalid={!!errors.renewalTermMonths} value={state.renewalTermMonths} onChange={(e) => set("renewalTermMonths", e.target.value)} />}
          </Field>
        )}
        <Field label="Notice period" error={errors.notice} hint={hint("noticePeriodDays") ?? "How much notice you must give before the end date to cancel or renegotiate."}>
          {(id, d) => (
            <div className="flex gap-2">
              <TextInput id={id} name="notice" inputMode="numeric" aria-describedby={d} invalid={!!errors.notice} value={state.notice} onChange={(e) => set("notice", e.target.value)} placeholder="30" />
              <Select aria-label="Notice period unit" name="noticeUnit" value={state.noticeUnit} onChange={(e) => set("noticeUnit", e.target.value as NoticeUnit)} className="w-32">
                <option value="days">days</option>
                <option value="weeks">weeks</option>
                <option value="months">months</option>
              </Select>
            </div>
          )}
        </Field>
        <div className="space-y-2 sm:col-span-2" aria-live="polite">
          {deadline && (
            <p className="rounded-lg border border-accent/25 bg-accent-soft px-3 py-2 text-sm text-accent-soft-fg">
              Notice deadline: <strong className="font-semibold">{formatDate(deadline)}</strong>. We'll remind you 90, 30 and 7 days before.
            </p>
          )}
          {warnings.map((w) => (
            <p key={w} className="text-sm text-warn-fg">
              ⚠ {w}
            </p>
          ))}
        </div>
      </section>

      <section className="space-y-3 border-t border-line pt-6" aria-labelledby="people-heading">
        <h2 id="people-heading" className="text-base font-semibold tracking-tight">
          People and companies
        </h2>
        <p className="text-xs text-faint">Everyone involved: {typeFor(state.type) ? typeFor(state.type)!.roles.join(", ").toLowerCase() : "the other side, guarantors, witnesses"}.</p>
        <PartiesEditor rows={state.parties} type={state.type} found={suggestions?.parties?.value} onChange={(rows) => set("parties", rows)} />
        {errors.parties && (
          <p role="alert" className="text-sm text-bad">
            {errors.parties}
          </p>
        )}
      </section>

      <section className="space-y-3 border-t border-line pt-6" aria-labelledby="terms-heading">
        <h2 id="terms-heading" className="text-base font-semibold tracking-tight">
          Key terms
        </h2>
        <p className="text-xs text-faint">Add only the terms that matter for this contract. They're searchable.</p>
        <TermsEditor rows={state.details} type={state.type} currency={state.currency} error={errors.details} aiKeys={aiKeys} found={suggestions?.details} onChange={(rows) => set("details", rows)} />
      </section>

      <section className="grid gap-4 border-t border-line pt-6">
        <Field label="Tags" error={errors.tags} hint="Separate with commas, e.g. vendor, saas.">
          {(id, d) => <TextInput id={id} name="tags" aria-describedby={d} invalid={!!errors.tags} value={state.tags} onChange={(e) => set("tags", e.target.value)} />}
        </Field>
        <Field label="Notes" error={errors.notes}>
          {(id, d) => <TextArea id={id} name="notes" rows={3} aria-describedby={d} invalid={!!errors.notes} value={state.notes} onChange={(e) => set("notes", e.target.value)} />}
        </Field>
      </section>

      {saveError && <Alert tone="bad">{saveError}</Alert>}
      <div className="flex flex-wrap gap-3 border-t border-line pt-6">
        <Button type="submit" disabled={saving}>
          {saving ? "Saving…" : submitLabel}
        </Button>
        {onCancel && (
          <Button variant="secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
